import React, { useMemo } from 'react'
import { Chart } from 'react-chartjs-2'
import { ChartOptions } from 'chart.js'
import { MonitorDashboardResp } from 'domain/payload/response/monitor/MonitorDashboardResp'
import { MonitorByAreaResponse } from 'domain/payload/response/monitor/MonitorByAreaResp'

type MonitorChartSource = Pick<
  MonitorDashboardResp,
  'progressTracker'
> | Pick<
  MonitorByAreaResponse,
  'progressTracker'
>

interface MonitorProgressChartProps {
  source: MonitorChartSource
  height?: number
}

const MonitorProgressChart: React.FC<MonitorProgressChartProps> = ({
  source,
  height = 320,
}) => {
  const trackerData = useMemo(() => {
    if (!source?.progressTracker?.length) return null

    const labels = source.progressTracker.map(t => t.date)

    const lineLabelsMap = new Map()
    const barLabelsMap = new Map()

    source.progressTracker.forEach(pt => {
      pt.lineTracker.forEach(lt => {
        if (!lineLabelsMap.has(lt.label)) {
          lineLabelsMap.set(lt.label, {
            type: 'line',
            label: lt.label,
            borderColor: lt.color,
            data: [],
            borderWidth: 2,
            fill: false,
            yAxisID: 'yRight',
            pointRadius: 1,
            pointHoverRadius: 5,
            hidden: lt.hidden,
          })
        }
        lineLabelsMap.get(lt.label).data.push(lt.value)
      })

      pt.barTracker.forEach(bt => {
        if (!barLabelsMap.has(bt.label)) {
          barLabelsMap.set(bt.label, {
            type: 'bar',
            label: bt.label,
            backgroundColor: bt.color,
            data: [],
            yAxisID: 'yLeft',
            hidden: bt.hidden,
          })
        }
        barLabelsMap.get(bt.label).data.push(bt.value)
      })
    })

    return {
      labels,
      datasets: [...lineLabelsMap.values(), ...barLabelsMap.values()],
    }
  }, [source])

  const options: ChartOptions<'bar' | 'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
    },
    scales: {
      yLeft: {
        type: 'linear',
        position: 'left',
        beginAtZero: true,
        title: {
          display: true,
          text: 'Total Akuisisi per Hari',
        },
        grid: {
          drawOnChartArea: false,
        },
      },
      yRight: {
        type: 'linear',
        position: 'right',
        beginAtZero: true,
        title: {
          display: true,
          text: 'Progress Keseluruhan',
        },
      },
    },
  }

  if (!trackerData) return null

  return (
    <div style={{ width: '100%', height }}>
      <Chart
        type="bar"
        data={trackerData}
        options={options}
      />
    </div>
  )
}

export default MonitorProgressChart
